<template>
  <div class="questionnaire-design-container">
    <el-card class="page-header-card">
      <div class="page-header">
        <div class="header-left">
          <h2>{{ isEdit ? '编辑问卷' : '创建新问卷' }}</h2>
          <el-tag type="info" v-if="questionnaire.status === 'draft'">草稿</el-tag>
          <el-tag type="success" v-else-if="questionnaire.status === 'published'">已发布</el-tag>
          <el-tag type="danger" v-else-if="questionnaire.status === 'inactive'">已停用</el-tag>
        </div>
        <div class="header-right">
          <el-button type="default" icon="el-icon-view" @click="previewQuestionnaire"
            >预览</el-button
          >
          <el-button type="primary" icon="el-icon-document-checked" @click="validateAndSave(false)"
            >保存</el-button
          >
          <el-button
            type="success"
            icon="el-icon-s-promotion"
            @click="validateAndSave(true)"
            :disabled="questionnaire.status === 'published'"
            >保存并发布</el-button
          >
        </div>
      </div>
    </el-card>

    <el-card class="questionnaire-content">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane label="基本信息" name="basic">
          <el-form
            ref="basicForm"
            :model="questionnaire"
            :rules="rules.basic"
            label-width="120px"
            class="questionnaire-form"
          >
            <el-form-item label="问卷名称" prop="title">
              <el-input
                v-model="questionnaire.title"
                placeholder="请输入问卷名称"
                maxlength="100"
                show-word-limit
              ></el-input>
            </el-form-item>

            <el-form-item label="问卷类型" prop="type">
              <el-select
                v-model="questionnaire.type"
                placeholder="请选择问卷类型"
                style="width: 100%"
              >
                <el-option
                  v-for="item in typeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="适用科室" prop="department">
              <el-select
                v-model="questionnaire.department"
                placeholder="请选择适用科室"
                style="width: 100%"
              >
                <el-option
                  v-for="item in departmentOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="适用疾病" prop="diseases">
              <el-select
                v-model="questionnaire.diseases"
                placeholder="请选择适用疾病"
                multiple
                style="width: 100%"
              >
                <el-option
                  v-for="item in diseaseOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="问卷描述" prop="description">
              <el-input
                type="textarea"
                v-model="questionnaire.description"
                placeholder="请输入问卷描述（介绍问卷的目的、填写说明等）"
                :rows="4"
                maxlength="500"
                show-word-limit
              ></el-input>
            </el-form-item>

            <el-form-item label="完成时间估计" prop="estimatedTime">
              <el-input-number
                v-model="questionnaire.estimatedTime"
                :min="1"
                :max="60"
                :step="1"
              ></el-input-number>
              <span class="form-item-hint">分钟</span>
            </el-form-item>

            <el-form-item label="问卷有效期">
              <el-date-picker
                v-model="questionnaire.validPeriod"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              ></el-date-picker>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="题目设计" name="questions">
          <div class="questions-toolbar">
            <el-button-group>
              <el-button type="primary" plain icon="el-icon-plus" @click="showQuestionTypeSelector"
                >添加题目</el-button
              >
              <el-button type="primary" plain icon="el-icon-upload2" @click="importQuestions"
                >导入题目</el-button
              >
            </el-button-group>

            <div class="right-tools">
              <el-button type="text" icon="el-icon-sort" @click="sortQuestions">重新排序</el-button>
              <el-button
                type="text"
                icon="el-icon-delete"
                :disabled="!canDeleteAll"
                @click="deleteAllQuestions"
                >全部删除</el-button
              >
            </div>
          </div>

          <el-empty
            v-if="questionnaire.questions.length === 0"
            description="暂无题目，请点击'添加题目'按钮"
          ></el-empty>

          <div v-else class="questions-container">
            <draggable
              v-model="questionnaire.questions"
              handle=".drag-handle"
              @start="drag = true"
              @end="drag = false"
            >
              <transition-group>
                <div
                  v-for="(question, index) in questionnaire.questions"
                  :key="index"
                  class="question-item"
                >
                  <div class="question-header">
                    <div class="left-section">
                      <i class="el-icon-rank drag-handle"></i>
                      <span class="question-number">Q{{ index + 1 }}</span>
                      <el-tag size="mini" :type="getQuestionTypeTagType(question.type)">{{
                        getQuestionTypeName(question.type)
                      }}</el-tag>
                    </div>
                    <div class="right-section">
                      <el-tooltip content="上移" placement="top" :disabled="index === 0">
                        <el-button
                          type="text"
                          icon="el-icon-top"
                          :disabled="index === 0"
                          @click="moveQuestion(index, 'up')"
                        ></el-button>
                      </el-tooltip>
                      <el-tooltip
                        content="下移"
                        placement="top"
                        :disabled="index === questionnaire.questions.length - 1"
                      >
                        <el-button
                          type="text"
                          icon="el-icon-bottom"
                          :disabled="index === questionnaire.questions.length - 1"
                          @click="moveQuestion(index, 'down')"
                        ></el-button>
                      </el-tooltip>
                      <el-tooltip content="复制" placement="top">
                        <el-button
                          type="text"
                          icon="el-icon-document-copy"
                          @click="duplicateQuestion(index)"
                        ></el-button>
                      </el-tooltip>
                      <el-tooltip content="删除" placement="top">
                        <el-button
                          type="text"
                          icon="el-icon-delete"
                          @click="deleteQuestion(index)"
                        ></el-button>
                      </el-tooltip>
                    </div>
                  </div>

                  <div class="question-content">
                    <el-form label-position="top" :model="question" class="question-form">
                      <el-form-item label="题目">
                        <el-input
                          v-model="question.title"
                          type="textarea"
                          :rows="2"
                          placeholder="请输入题目内容"
                          :maxlength="200"
                          show-word-limit
                        ></el-input>
                      </el-form-item>

                      <el-form-item label="题目说明" v-if="hasDescription(question.type)">
                        <el-input
                          v-model="question.description"
                          type="textarea"
                          :rows="1"
                          placeholder="题目补充说明，选填"
                          :maxlength="100"
                          show-word-limit
                        ></el-input>
                      </el-form-item>

                      <!-- 单选题选项 -->
                      <template v-if="question.type === 'radio'">
                        <div class="options-header">
                          <span>选项</span>
                          <el-button type="text" icon="el-icon-plus" @click="addOption(question)"
                            >添加选项</el-button
                          >
                        </div>
                        <el-form-item
                          v-for="(option, optIndex) in question.options"
                          :key="'option-' + optIndex"
                          class="option-item"
                        >
                          <el-radio :label="optIndex" :disabled="true">
                            <el-input
                              v-model="question.options[optIndex]"
                              placeholder="请输入选项内容"
                              size="small"
                              style="width: 80%"
                            ></el-input>
                            <el-button
                              type="text"
                              icon="el-icon-delete"
                              @click.stop="removeOption(question, optIndex)"
                              :disabled="question.options.length <= 2"
                            ></el-button>
                          </el-radio>
                        </el-form-item>
                        <el-checkbox v-model="question.hasOther" class="other-option">
                          包含"其他"选项
                          <el-input
                            v-if="question.hasOther"
                            v-model="question.otherText"
                            placeholder="其他选项文字"
                            size="mini"
                            style="width: 200px; margin-left: 10px"
                          ></el-input>
                        </el-checkbox>
                      </template>

                      <!-- 多选题选项 -->
                      <template v-if="question.type === 'checkbox'">
                        <div class="options-header">
                          <span>选项</span>
                          <el-button type="text" icon="el-icon-plus" @click="addOption(question)"
                            >添加选项</el-button
                          >
                        </div>
                        <el-form-item
                          v-for="(option, optIndex) in question.options"
                          :key="'option-' + optIndex"
                          class="option-item"
                        >
                          <el-checkbox :label="optIndex" :disabled="true">
                            <el-input
                              v-model="question.options[optIndex]"
                              placeholder="请输入选项内容"
                              size="small"
                              style="width: 80%"
                            ></el-input>
                            <el-button
                              type="text"
                              icon="el-icon-delete"
                              @click.stop="removeOption(question, optIndex)"
                              :disabled="question.options.length <= 2"
                            ></el-button>
                          </el-checkbox>
                        </el-form-item>
                        <div class="checkbox-settings">
                          <el-checkbox v-model="question.hasOther" class="other-option">
                            包含"其他"选项
                            <el-input
                              v-if="question.hasOther"
                              v-model="question.otherText"
                              placeholder="其他选项文字"
                              size="mini"
                              style="width: 200px; margin-left: 10px"
                            ></el-input>
                          </el-checkbox>

                          <el-form-item label="最少选择" class="min-max-select">
                            <el-input-number
                              v-model="question.minSelect"
                              :min="0"
                              :max="question.options.length"
                              size="mini"
                            ></el-input-number>
                          </el-form-item>
                          <el-form-item label="最多选择" class="min-max-select">
                            <el-input-number
                              v-model="question.maxSelect"
                              :min="0"
                              :max="question.options.length"
                              size="mini"
                            ></el-input-number>
                          </el-form-item>
                        </div>
                      </template>

                      <!-- 填空题 -->
                      <template v-if="question.type === 'text'">
                        <el-form-item label="输入框类型">
                          <el-radio-group v-model="question.inputType" size="medium">
                            <el-radio-button label="singleLine">单行文本</el-radio-button>
                            <el-radio-button label="multiLine">多行文本</el-radio-button>
                          </el-radio-group>
                        </el-form-item>
                        <el-form-item label="预设提示文本">
                          <el-input
                            v-model="question.placeholder"
                            placeholder="请输入提示文本"
                          ></el-input>
                        </el-form-item>
                        <el-form-item v-if="question.inputType === 'multiLine'" label="行数">
                          <el-input-number
                            v-model="question.rows"
                            :min="2"
                            :max="10"
                            size="small"
                          ></el-input-number>
                        </el-form-item>
                      </template>

                      <!-- 数字题 -->
                      <template v-if="question.type === 'number'">
                        <el-row :gutter="20">
                          <el-col :span="8">
                            <el-form-item label="最小值">
                              <el-input-number v-model="question.min" :step="1"></el-input-number>
                            </el-form-item>
                          </el-col>
                          <el-col :span="8">
                            <el-form-item label="最大值">
                              <el-input-number v-model="question.max" :step="1"></el-input-number>
                            </el-form-item>
                          </el-col>
                          <el-col :span="8">
                            <el-form-item label="单位">
                              <el-input
                                v-model="question.unit"
                                placeholder="例如: kg, mm 等"
                              ></el-input>
                            </el-form-item>
                          </el-col>
                        </el-row>
                        <el-form-item label="预设提示文本">
                          <el-input
                            v-model="question.placeholder"
                            placeholder="请输入提示文本"
                          ></el-input>
                        </el-form-item>
                      </template>

                      <!-- 日期题 -->
                      <template v-if="question.type === 'date'">
                        <el-form-item label="日期类型">
                          <el-radio-group v-model="question.dateType">
                            <el-radio-button label="date">日期</el-radio-button>
                            <el-radio-button label="datetime">日期时间</el-radio-button>
                          </el-radio-group>
                        </el-form-item>
                        <el-row :gutter="20">
                          <el-col :span="12">
                            <el-form-item label="起始日期">
                              <el-date-picker
                                v-model="question.startDate"
                                :type="question.dateType === 'date' ? 'date' : 'datetime'"
                                placeholder="限制起始日期"
                                style="width: 100%"
                              ></el-date-picker>
                            </el-form-item>
                          </el-col>
                          <el-col :span="12">
                            <el-form-item label="截止日期">
                              <el-date-picker
                                v-model="question.endDate"
                                :type="question.dateType === 'date' ? 'date' : 'datetime'"
                                placeholder="限制截止日期"
                                style="width: 100%"
                              ></el-date-picker>
                            </el-form-item>
                          </el-col>
                        </el-row>
                      </template>

                      <!-- 评分题 -->
                      <template v-if="question.type === 'rate'">
                        <el-form-item label="评分样式">
                          <el-radio-group v-model="question.rateType">
                            <el-radio label="star">星级</el-radio>
                            <el-radio label="number">数字</el-radio>
                          </el-radio-group>
                        </el-form-item>
                        <el-form-item label="最高分值">
                          <el-input-number
                            v-model="question.maxRate"
                            :min="3"
                            :max="10"
                          ></el-input-number>
                        </el-form-item>
                        <el-form-item v-if="question.rateType === 'number'" label="评分标签">
                          <el-row :gutter="10">
                            <el-col :span="12">
                              <el-input
                                v-model="question.lowRateText"
                                placeholder="最低分描述文字，如：不满意"
                              ></el-input>
                            </el-col>
                            <el-col :span="12">
                              <el-input
                                v-model="question.highRateText"
                                placeholder="最高分描述文字，如：非常满意"
                              ></el-input>
                            </el-col>
                          </el-row>
                        </el-form-item>
                      </template>

                      <!-- 矩阵题 -->
                      <template v-if="question.type === 'matrix'">
                        <div class="matrix-design">
                          <div class="matrix-rows">
                            <div class="matrix-title">
                              <span>行标题</span>
                              <el-button
                                type="text"
                                icon="el-icon-plus"
                                @click="addMatrixRow(question)"
                                >添加行</el-button
                              >
                            </div>
                            <div
                              v-for="(row, rowIndex) in question.rows"
                              :key="'row-' + rowIndex"
                              class="matrix-label-item"
                            >
                              <el-input
                                v-model="question.rows[rowIndex]"
                                placeholder="行标题"
                                size="small"
                              ></el-input>
                              <el-button
                                type="text"
                                icon="el-icon-delete"
                                @click="removeMatrixRow(question, rowIndex)"
                                :disabled="question.rows.length <= 1"
                              ></el-button>
                            </div>
                          </div>

                          <div class="matrix-cols">
                            <div class="matrix-title">
                              <span>列选项</span>
                              <el-button
                                type="text"
                                icon="el-icon-plus"
                                @click="addMatrixCol(question)"
                                >添加列</el-button
                              >
                            </div>
                            <div
                              v-for="(col, colIndex) in question.cols"
                              :key="'col-' + colIndex"
                              class="matrix-label-item"
                            >
                              <el-input
                                v-model="question.cols[colIndex]"
                                placeholder="列选项"
                                size="small"
                              ></el-input>
                              <el-button
                                type="text"
                                icon="el-icon-delete"
                                @click="removeMatrixCol(question, colIndex)"
                                :disabled="question.cols.length <= 2"
                              ></el-button>
                            </div>
                          </div>
                        </div>

                        <el-form-item label="矩阵类型">
                          <el-radio-group v-model="question.matrixType">
                            <el-radio label="radio">单选(每行只能选择一个选项)</el-radio>
                            <el-radio label="checkbox">多选(每行可选择多个选项)</el-radio>
                          </el-radio-group>
                        </el-form-item>
                      </template>

                      <div class="question-settings">
                        <el-checkbox v-model="question.required">必填题</el-checkbox>
                        <el-checkbox
                          v-model="question.randomOptions"
                          v-if="hasOptions(question.type)"
                          >选项随机排序</el-checkbox
                        >
                        <el-checkbox v-model="question.showNumber">显示题号</el-checkbox>
                      </div>

                      <div
                        class="logic-jump"
                        v-if="
                          canHaveLogicJump(question.type) &&
                          index < questionnaire.questions.length - 1
                        "
                      >
                        <el-button type="text" @click="question.showLogic = !question.showLogic">
                          {{ question.showLogic ? '隐藏逻辑跳转' : '设置逻辑跳转' }}
                          <i
                            :class="question.showLogic ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
                          ></i>
                        </el-button>
                        <div v-if="question.showLogic" class="logic-rules">
                          <el-divider content-position="left">逻辑跳转规则</el-divider>
                          <div
                            v-for="(rule, ruleIndex) in question.logicRules"
                            :key="'rule-' + ruleIndex"
                            class="logic-rule-item"
                          >
                            <el-row :gutter="10">
                              <el-col :span="8">
                                <el-form-item label="当选择" label-width="80px">
                                  <el-select
                                    v-model="rule.optionIndex"
                                    placeholder="请选择选项"
                                    multiple
                                    :multiple-limit="question.type === 'radio' ? 1 : 0"
                                  >
                                    <el-option
                                      v-for="(opt, idx) in question.options"
                                      :key="idx"
                                      :label="opt"
                                      :value="idx"
                                    ></el-option>
                                    <el-option
                                      v-if="question.hasOther"
                                      key="other"
                                      :label="question.otherText || '其他'"
                                      value="other"
                                    ></el-option>
                                  </el-select>
                                </el-form-item>
                              </el-col>
                              <el-col :span="8">
                                <el-form-item label="跳转至" label-width="80px">
                                  <el-select v-model="rule.jumpTo" placeholder="请选择跳转的题目">
                                    <el-option
                                      v-for="(q, qIndex) in questionnaire.questions.slice(
                                        index + 1
                                      )"
                                      :key="qIndex + index + 1"
                                      :label="`Q${qIndex + index + 2}: ${q.title.slice(0, 20)}${
                                        q.title.length > 20 ? '...' : ''
                                      }`"
                                      :value="qIndex + index + 1"
                                    ></el-option>
                                    <el-option key="end" label="问卷末尾" value="end"></el-option>
                                  </el-select>
                                </el-form-item>
                              </el-col>
                              <el-col :span="4">
                                <el-button
                                  type="danger"
                                  icon="el-icon-delete"
                                  size="mini"
                                  @click="removeLogicRule(question, ruleIndex)"
                                ></el-button>
                              </el-col>
                            </el-row>
                          </div>

                          <div class="add-rule">
                            <el-button
                              type="primary"
                              plain
                              size="small"
                              icon="el-icon-plus"
                              @click="addLogicRule(question)"
                              >添加规则</el-button
                            >
                          </div>
                        </div>
                      </div>
                    </el-form>
                  </div>
                </div>
              </transition-group>
            </draggable>
          </div>
        </el-tab-pane>

        <el-tab-pane label="结束页设置" name="end">
          <el-form
            ref="endForm"
            :model="questionnaire.endPage"
            label-width="120px"
            class="questionnaire-form"
          >
            <el-form-item label="显示结束页">
              <el-switch v-model="questionnaire.endPage.enabled"></el-switch>
            </el-form-item>

            <template v-if="questionnaire.endPage.enabled">
              <el-form-item label="标题">
                <el-input
                  v-model="questionnaire.endPage.title"
                  placeholder="例如：问卷已提交，感谢您的参与！"
                  maxlength="100"
                  show-word-limit
                ></el-input>
              </el-form-item>

              <el-form-item label="内容">
                <el-input
                  type="textarea"
                  v-model="questionnaire.endPage.content"
                  placeholder="感谢信息或其他说明"
                  :rows="4"
                  maxlength="500"
                  show-word-limit
                ></el-input>
              </el-form-item>

              <el-form-item label="按钮文字">
                <el-input
                  v-model="questionnaire.endPage.buttonText"
                  placeholder="例如：返回主页"
                  style="width: 200px"
                ></el-input>
              </el-form-item>

              <el-form-item label="跳转链接">
                <el-input
                  v-model="questionnaire.endPage.redirectUrl"
                  placeholder="填写完成后跳转的URL，如果留空则不跳转"
                ></el-input>
              </el-form-item>
            </template>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 添加题目选择器 -->
    <el-dialog
      title="请选择要添加的题目类型"
      :visible.sync="questionTypeSelectorVisible"
      width="600px"
      center
    >
      <div class="question-type-grid">
        <div
          v-for="type in questionTypes"
          :key="type.value"
          class="question-type-item"
          @click="addQuestion(type.value)"
        >
          <div class="type-icon">
            <i :class="type.icon"></i>
          </div>
          <div class="type-info">
            <div class="type-name">{{ type.label }}</div>
            <div class="type-desc">{{ type.description }}</div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 问卷预览对话框 -->
    <el-dialog
      title="问卷预览"
      :visible.sync="previewDialogVisible"
      width="70%"
      :fullscreen="isFullscreen"
      :before-close="closePreview"
    >
      <div class="preview-toolbar">
        <el-button type="text" @click="toggleFullscreen">
          <i :class="isFullscreen ? 'el-icon-crop' : 'el-icon-full-screen'"></i>
          {{ isFullscreen ? '退出全屏' : '全屏预览' }}
        </el-button>
      </div>

      <div class="questionnaire-preview">
        <h1 class="preview-title">{{ questionnaire.title }}</h1>
        <div class="preview-description" v-if="questionnaire.description">
          {{ questionnaire.description }}
        </div>

        <div class="preview-questions">
          <div
            v-for="(question, index) in questionnaire.questions"
            :key="index"
            class="preview-question-item"
          >
            <div class="preview-question-title">
              <span v-if="question.showNumber" class="question-number">Q{{ index + 1 }}. </span>
              {{ question.title }}
              <span class="required-mark" v-if="question.required">*</span>
            </div>
            <div class="preview-question-desc" v-if="question.description">
              {{ question.description }}
            </div>

            <div class="preview-question-content">
              <!-- 单选题预览 -->
              <template v-if="question.type === 'radio'">
                <el-radio-group>
                  <div
                    v-for="(option, optIndex) in question.options"
                    :key="optIndex"
                    class="preview-option"
                  >
                    <el-radio :label="optIndex" disabled>{{ option }}</el-radio>
                  </div>
                  <div v-if="question.hasOther" class="preview-option">
                    <el-radio label="other" disabled>
                      {{ question.otherText || '其他' }}
                      <el-input
                        size="small"
                        style="width: 200px; margin-left: 10px"
                        disabled
                      ></el-input>
                    </el-radio>
                  </div>
                </el-radio-group>
              </template>

              <!-- 多选题预览 -->
              <template v-else-if="question.type === 'checkbox'">
                <el-checkbox-group>
                  <div
                    v-for="(option, optIndex) in question.options"
                    :key="optIndex"
                    class="preview-option"
                  >
                    <el-checkbox :label="optIndex" disabled>{{ option }}</el-checkbox>
                  </div>
                  <div v-if="question.hasOther" class="preview-option">
                    <el-checkbox label="other" disabled>
                      {{ question.otherText || '其他' }}
                      <el-input
                        size="small"
                        style="width: 200px; margin-left: 10px"
                        disabled
                      ></el-input>
                    </el-checkbox>
                  </div>
                </el-checkbox-group>
              </template>

              <!-- 填空题预览 -->
              <template v-else-if="question.type === 'text'">
                <el-input
                  v-if="question.inputType === 'singleLine'"
                  :placeholder="question.placeholder"
                  disabled
                ></el-input>
                <el-input
                  v-else
                  type="textarea"
                  :rows="question.rows || 3"
                  :placeholder="question.placeholder"
                  disabled
                ></el-input>
              </template>

              <!-- 数字题预览 -->
              <template v-else-if="question.type === 'number'">
                <div class="number-input-preview">
                  <el-input-number
                    :min="question.min"
                    :max="question.max"
                    :step="1"
                    :placeholder="question.placeholder"
                    disabled
                  ></el-input-number>
                  <span v-if="question.unit" class="unit-label">{{ question.unit }}</span>
                </div>
              </template>

              <!-- 日期题预览 -->
              <template v-else-if="question.type === 'date'">
                <el-date-picker
                  :type="question.dateType === 'date' ? 'date' : 'datetime'"
                  placeholder="请选择日期"
                  style="width: 100%"
                  disabled
                ></el-date-picker>
              </template>

              <!-- 评分题预览 -->
              <template v-else-if="question.type === 'rate'">
                <div class="rate-preview">
                  <el-rate
                    v-if="question.rateType === 'star'"
                    :max="question.maxRate"
                    disabled
                  ></el-rate>
                  <div v-else class="number-rate">
                    <div class="rate-labels">
                      <span class="low-rate">{{ question.lowRateText || '最低分' }}</span>
                      <span class="high-rate">{{ question.highRateText || '最高分' }}</span>
                    </div>
                    <el-rate
                      :max="question.maxRate"
                      :low-threshold="Math.floor(question.maxRate / 3)"
                      :high-threshold="Math.floor((question.maxRate * 2) / 3)"
                      :colors="['#F7BA2A', '#F7BA2A', '#F7BA2A']"
                      :icon-classes="[
                        'el-icon-circle-check',
                        'el-icon-circle-check',
                        'el-icon-circle-check',
                      ]"
                      :void-icon-class="'el-icon-circle-check'"
                      :score-template="`{value}/${question.maxRate}`"
                      disabled
                      show-score
                    ></el-rate>
                  </div>
                </div>
              </template>

              <!-- 矩阵题预览 -->
              <template v-else-if="question.type === 'matrix'">
                <div class="matrix-preview">
                  <table class="matrix-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th v-for="(col, colIndex) in question.cols" :key="colIndex">{{ col }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rowIndex) in question.rows" :key="rowIndex">
                        <td>{{ row }}</td>
                        <td v-for="(col, colIndex) in question.cols" :key="colIndex">
                          <el-radio
                            v-if="question.matrixType === 'radio'"
                            :label="colIndex"
                            disabled
                            class="matrix-radio"
                          ></el-radio>
                          <el-checkbox
                            v-else
                            :label="colIndex"
                            disabled
                            class="matrix-checkbox"
                          ></el-checkbox>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="preview-end-page" v-if="questionnaire.endPage.enabled">
          <h2>{{ questionnaire.endPage.title || '感谢您的参与' }}</h2>
          <p>{{ questionnaire.endPage.content }}</p>
          <el-button type="primary">{{ questionnaire.endPage.buttonText || '完成' }}</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 导入题目对话框 -->
    <el-dialog title="导入题目" :visible.sync="importDialogVisible" width="500px">
      <el-tabs v-model="importTab">
        <el-tab-pane label="从题库导入" name="library">
          <div class="import-from-library">
            <el-form :inline="true" size="small">
              <el-form-item label="题目类型">
                <el-select v-model="libraryFilter.type" placeholder="全部类型" clearable>
                  <el-option
                    v-for="item in questionTypes"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="关键词">
                <el-input v-model="libraryFilter.keyword" placeholder="请输入关键词"></el-input>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="searchFromLibrary"
                  >搜索</el-button
                >
              </el-form-item>
            </el-form>

            <el-table
              ref="libraryTable"
              :data="libraryQuestions"
              height="240"
              border
              @selection-change="handleLibrarySelectionChange"
            >
              <el-table-column type="selection" width="55"> </el-table-column>
              <el-table-column prop="title" label="题目内容" show-overflow-tooltip>
              </el-table-column>
              <el-table-column prop="typeText" label="题目类型" width="100"> </el-table-column>
            </el-table>

            <div class="import-footer">
              <el-pagination
                small
                layout="prev, pager, next"
                :total="100"
                :page-size="10"
                @current-change="handleLibraryPageChange"
              >
              </el-pagination>
              <el-button
                type="primary"
                size="small"
                @click="importFromLibrary"
                :disabled="!selectedLibraryQuestions.length"
              >
                导入选中题目
              </el-button>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="从文件导入" name="file">
          <div class="import-from-file">
            <el-upload
              class="upload-demo"
              drag
              action="#"
              :http-request="handleFileUpload"
              :multiple="false"
              :show-file-list="false"
              :limit="1"
              accept=".json,.xlsx"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
              <div class="el-upload__tip" slot="tip">支持 .json 或 .xlsx 格式的文件</div>
            </el-upload>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- 排序对话框 -->
    <el-dialog title="重新排序题目" :visible.sync="sortDialogVisible" width="500px">
      <el-alert
        title="提示：拖动题目可以调整顺序"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 15px"
      ></el-alert>

      <draggable v-model="sortableQuestions" handle=".sort-drag-handle">
        <transition-group>
          <div v-for="(question, index) in sortableQuestions" :key="index" class="sort-item">
            <span class="sort-drag-handle">
              <i class="el-icon-rank"></i>
            </span>
            <span class="sort-number">{{ index + 1 }}</span>
            <span class="sort-title">{{ question.title }}</span>
          </div>
        </transition-group>
      </draggable>

      <div slot="footer" class="dialog-footer">
        <el-button @click="sortDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="applySortChanges">应 用</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'QuestionnaireCreate',
  components: {
    draggable,
  },
  data() {
    return {
      // 主要数据模型
      questionnaire: {
        id: '',
        title: '',
        description: '',
        type: '',
        department: '',
        diseases: [],
        estimatedTime: 5,
        validPeriod: [],
        status: 'draft',
        questions: [],
        endPage: {
          enabled: true,
          title: '问卷已提交，感谢您的参与！',
          content: '感谢您抽出宝贵时间完成本次问卷调查，您的反馈对我们非常重要。',
          buttonText: '完成',
          redirectUrl: '',
        },
      },

      // 表单验证规则
      rules: {
        basic: {
          title: [
            { required: true, message: '请输入问卷名称', trigger: 'blur' },
            { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' },
          ],
          type: [{ required: true, message: '请选择问卷类型', trigger: 'change' }],
          department: [{ required: true, message: '请选择适用科室', trigger: 'change' }],
        },
      },

      // 页面控制
      activeTab: 'basic',
      isEdit: false,
      drag: false,

      // 对话框控制
      questionTypeSelectorVisible: false,
      previewDialogVisible: false,
      isFullscreen: false,
      importDialogVisible: false,
      importTab: 'library',
      sortDialogVisible: false,

      // 选项数据
      questionTypes: [
        {
          value: 'radio',
          label: '单选题',
          icon: 'el-icon-circle-check',
          description: '只能选择一个选项的问题',
        },
        {
          value: 'checkbox',
          label: '多选题',
          icon: 'el-icon-check',
          description: '可选择多个选项的问题',
        },
        {
          value: 'text',
          label: '填空题',
          icon: 'el-icon-edit-outline',
          description: '可自由输入文本的问题',
        },
        {
          value: 'number',
          label: '数字题',
          icon: 'el-icon-data-analysis',
          description: '只能输入数字的问题',
        },
        {
          value: 'date',
          label: '日期题',
          icon: 'el-icon-date',
          description: '选择日期或日期时间的问题',
        },
        {
          value: 'rate',
          label: '评分题',
          icon: 'el-icon-star-on',
          description: '使用评分进行打分的问题',
        },
        {
          value: 'matrix',
          label: '矩阵题',
          icon: 'el-icon-menu',
          description: '多行多列的矩阵选择问题',
        },
      ],
      typeOptions: [
        { value: 'satisfaction', label: '满意度调查' },
        { value: 'followup', label: '随访问卷' },
        { value: 'screening', label: '筛查问卷' },
        { value: 'evaluation', label: '评估量表' },
        { value: 'registration', label: '登记表' },
        { value: 'feedback', label: '意见反馈' },
      ],
      departmentOptions: [
        { value: 'oncology', label: '肿瘤科' },
        { value: 'cardiology', label: '心血管内科' },
        { value: 'neurology', label: '神经内科' },
        { value: 'respiratory', label: '呼吸内科' },
        { value: 'gastroenterology', label: '消化内科' },
        { value: 'nephrology', label: '肾内科' },
        { value: 'endocrinology', label: '内分泌科' },
        { value: 'all', label: '全院通用' },
      ],
      diseaseOptions: [
        { value: 'lung_cancer', label: '肺癌' },
        { value: 'liver_cancer', label: '肝癌' },
        { value: 'breast_cancer', label: '乳腺癌' },
        { value: 'colorectal_cancer', label: '结直肠癌' },
        { value: 'gastric_cancer', label: '胃癌' },
        { value: 'esophageal_cancer', label: '食道癌' },
        { value: 'prostate_cancer', label: '前列腺癌' },
        { value: 'hypertension', label: '高血压' },
        { value: 'diabetes', label: '糖尿病' },
        { value: 'coronary_heart_disease', label: '冠心病' },
        { value: 'stroke', label: '脑卒中' },
        { value: 'copd', label: '慢阻肺' },
      ],

      // 题目库相关
      libraryFilter: {
        type: '',
        keyword: '',
      },
      libraryQuestions: [],
      selectedLibraryQuestions: [],

      // 排序相关
      sortableQuestions: [],
    }
  },
  computed: {
    canDeleteAll() {
      return this.questionnaire.questions && this.questionnaire.questions.length > 0
    },
  },
  created() {
    // 检查是否为编辑模式
    const questionnaireId = this.$route.query.id
    if (questionnaireId) {
      this.isEdit = true
      this.loadQuestionnaireData(questionnaireId)
    }

    // 模拟加载题目库数据
    this.loadLibraryQuestions()
  },
  methods: {
    // 加载问卷数据
    loadQuestionnaireData(id) {
      // 模拟API请求
      setTimeout(() => {
        // 模拟数据
        const data = {
          id: 'QN001',
          title: '肿瘤患者生活质量评估问卷',
          description:
            '本问卷用于评估肿瘤患者的生活质量和日常活动能力。请根据您近一周的实际情况进行填写，您的回答将帮助医生更好地了解您的健康状况。',
          type: 'evaluation',
          department: 'oncology',
          diseases: ['lung_cancer', 'breast_cancer'],
          estimatedTime: 8,
          validPeriod: ['2023-01-01', '2023-12-31'],
          status: 'draft',
          questions: [
            {
              type: 'radio',
              title: '您目前的整体健康状况如何?',
              description: '',
              required: true,
              showNumber: true,
              options: ['非常好', '良好', '一般', '较差', '非常差'],
              hasOther: false,
              otherText: '其他',
              randomOptions: false,
              showLogic: false,
              logicRules: [],
            },
            {
              type: 'checkbox',
              title: '您最近一个月内有以下哪些症状? (可多选)',
              description: '请根据您的真实感受选择',
              required: true,
              showNumber: true,
              options: ['疼痛', '疲劳', '恶心', '呕吐', '食欲不振', '失眠'],
              hasOther: true,
              otherText: '其他症状',
              randomOptions: false,
              minSelect: 0,
              maxSelect: 0,
              showLogic: false,
              logicRules: [],
            },
            {
              type: 'number',
              title: '请评估您的疼痛程度',
              description: '0表示无疼痛，10表示极度疼痛',
              required: true,
              showNumber: true,
              min: 0,
              max: 10,
              unit: '分',
              placeholder: '请输入0-10之间的数字',
              showLogic: false,
              logicRules: [],
            },
          ],
          endPage: {
            enabled: true,
            title: '问卷已提交，感谢您的参与！',
            content:
              '感谢您抽出宝贵时间完成本次问卷调查，您的反馈对我们非常重要。医生将会根据您的回答进行进一步评估。',
            buttonText: '完成',
            redirectUrl: '',
          },
        }

        // 赋值给表单
        this.questionnaire = data
      }, 500)
    },

    // 切换标签页
    handleTabClick(tab) {
      // 可以在这里处理标签页切换的逻辑
      console.log('Tab clicked:', tab.name)
    },

    // 显示题目类型选择器
    showQuestionTypeSelector() {
      this.questionTypeSelectorVisible = true
    },

    // 添加问题
    addQuestion(type) {
      // 创建新问题对象
      let newQuestion = {
        type: type,
        title: '',
        description: '',
        required: true,
        showNumber: true,
        showLogic: false,
        logicRules: [],
      }

      // 根据题型添加特定属性
      switch (type) {
        case 'radio':
          newQuestion.options = ['选项1', '选项2', '选项3']
          newQuestion.hasOther = false
          newQuestion.otherText = '其他'
          newQuestion.randomOptions = false
          break

        case 'checkbox':
          newQuestion.options = ['选项1', '选项2', '选项3']
          newQuestion.hasOther = false
          newQuestion.otherText = '其他'
          newQuestion.randomOptions = false
          newQuestion.minSelect = 0
          newQuestion.maxSelect = 0
          break

        case 'text':
          newQuestion.inputType = 'singleLine'
          newQuestion.placeholder = '请在此输入'
          newQuestion.rows = 3
          break

        case 'number':
          newQuestion.min = 0
          newQuestion.max = 100
          newQuestion.unit = ''
          newQuestion.placeholder = '请输入数字'
          break

        case 'date':
          newQuestion.dateType = 'date'
          newQuestion.startDate = ''
          newQuestion.endDate = ''
          break

        case 'rate':
          newQuestion.rateType = 'star'
          newQuestion.maxRate = 5
          newQuestion.lowRateText = '不满意'
          newQuestion.highRateText = '非常满意'
          break

        case 'matrix':
          newQuestion.rows = ['行标题1', '行标题2', '行标题3']
          newQuestion.cols = ['选项1', '选项2', '选项3']
          newQuestion.matrixType = 'radio'
          break
      }

      // 添加到问题列表
      this.questionnaire.questions.push(newQuestion)

      // 关闭选择器
      this.questionTypeSelectorVisible = false
    },

    // 移动问题
    moveQuestion(index, direction) {
      if (direction === 'up' && index > 0) {
        const temp = this.questionnaire.questions[index]
        this.$set(this.questionnaire.questions, index, this.questionnaire.questions[index - 1])
        this.$set(this.questionnaire.questions, index - 1, temp)
      } else if (direction === 'down' && index < this.questionnaire.questions.length - 1) {
        const temp = this.questionnaire.questions[index]
        this.$set(this.questionnaire.questions, index, this.questionnaire.questions[index + 1])
        this.$set(this.questionnaire.questions, index + 1, temp)
      }
    },

    // 复制问题
    duplicateQuestion(index) {
      const question = JSON.parse(JSON.stringify(this.questionnaire.questions[index]))
      // 修改标题，添加"(复制)"
      if (question.title) {
        question.title += ' (复制)'
      }
      // 插入到当前问题之后
      this.questionnaire.questions.splice(index + 1, 0, question)
    },

    // 删除问题
    deleteQuestion(index) {
      this.$confirm('确定删除此题目吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.questionnaire.questions.splice(index, 1)
          this.$message({
            type: 'success',
            message: '删除成功!',
          })
        })
        .catch(() => {})
    },

    // 删除所有问题
    deleteAllQuestions() {
      this.$confirm('确定删除所有题目吗? 此操作不可恢复!', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.questionnaire.questions = []
          this.$message({
            type: 'success',
            message: '已清空所有题目!',
          })
        })
        .catch(() => {})
    },

    // 添加选项
    addOption(question) {
      question.options.push(`选项${question.options.length + 1}`)
    },

    // 移除选项
    removeOption(question, index) {
      if (question.options.length > 2) {
        question.options.splice(index, 1)
      } else {
        this.$message.warning('至少保留两个选项')
      }
    },

    // 矩阵题添加行
    addMatrixRow(question) {
      question.rows.push(`行标题${question.rows.length + 1}`)
    },

    // 矩阵题移除行
    removeMatrixRow(question, index) {
      if (question.rows.length > 1) {
        question.rows.splice(index, 1)
      } else {
        this.$message.warning('至少保留一行')
      }
    },

    // 矩阵题添加列
    addMatrixCol(question) {
      question.cols.push(`选项${question.cols.length + 1}`)
    },

    // 矩阵题移除列
    removeMatrixCol(question, index) {
      if (question.cols.length > 2) {
        question.cols.splice(index, 1)
      } else {
        this.$message.warning('至少保留两列')
      }
    },

    // 添加逻辑跳转规则
    addLogicRule(question) {
      question.logicRules.push({
        optionIndex: question.type === 'radio' ? 0 : [0],
        jumpTo: 'end',
      })
    },

    // 删除逻辑跳转规则
    removeLogicRule(question, index) {
      question.logicRules.splice(index, 1)
    },

    // 获取问题类型标签样式
    getQuestionTypeTagType(type) {
      const typeMap = {
        radio: 'primary',
        checkbox: 'success',
        text: 'info',
        number: 'warning',
        date: '',
        rate: 'danger',
        matrix: '',
      }
      return typeMap[type] || ''
    },

    // 获取问题类型名称
    getQuestionTypeName(type) {
      const typeObj = this.questionTypes.find(t => t.value === type)
      return typeObj ? typeObj.label : type
    },

    // 判断题型是否有描述字段
    hasDescription(type) {
      return true // 所有题型都支持描述
    },

    // 判断题型是否有选项
    hasOptions(type) {
      return ['radio', 'checkbox'].includes(type)
    },

    // 判断题型是否支持逻辑跳转
    canHaveLogicJump(type) {
      return ['radio', 'checkbox'].includes(type)
    },

    // 预览问卷
    previewQuestionnaire() {
      this.previewDialogVisible = true
    },

    // 关闭预览
    closePreview() {
      this.isFullscreen = false
      this.previewDialogVisible = false
    },

    // 切换全屏预览
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },

    // 导入题目
    importQuestions() {
      this.importDialogVisible = true
    },

    // 加载题目库数据
    loadLibraryQuestions() {
      // 模拟API请求
      this.libraryQuestions = [
        { id: 1, title: '您对我们的服务满意度如何？', type: 'radio', typeText: '单选题' },
        {
          id: 2,
          title: '您最关注的健康问题是什么？（多选）',
          type: 'checkbox',
          typeText: '多选题',
        },
        { id: 3, title: '请描述您的主要症状：', type: 'text', typeText: '填空题' },
        { id: 4, title: '请评估您的疼痛程度（0-10分）', type: 'number', typeText: '数字题' },
        { id: 5, title: '您上次就诊的日期是？', type: 'date', typeText: '日期题' },
        { id: 6, title: '请对医生的诊疗服务进行评分', type: 'rate', typeText: '评分题' },
        { id: 7, title: '请评价以下方面的服务质量', type: 'matrix', typeText: '矩阵题' },
      ]
    },

    // 搜索题库
    searchFromLibrary() {
      // 模拟搜索功能
      console.log('搜索条件:', this.libraryFilter)
      // 实际应用中，这里应该调用API重新获取数据
    },

    // 处理题库分页
    handleLibraryPageChange(page) {
      console.log('切换到页码:', page)
      // 实际应用中，这里应该调用API获取对应页的数据
    },

    // 处理题库选择变化
    handleLibrarySelectionChange(selection) {
      this.selectedLibraryQuestions = selection
    },

    // 从题库导入题目
    importFromLibrary() {
      if (this.selectedLibraryQuestions.length === 0) {
        this.$message.warning('请选择要导入的题目')
        return
      }

      // 模拟导入
      for (const item of this.selectedLibraryQuestions) {
        // 根据类型创建不同的问题模板
        this.addQuestion(item.type)
        // 设置问题标题
        const index = this.questionnaire.questions.length - 1
        this.questionnaire.questions[index].title = item.title
      }

      this.$message.success(`已导入 ${this.selectedLibraryQuestions.length} 个题目`)
      this.importDialogVisible = false
    },

    // 处理文件上传
    handleFileUpload(options) {
      const file = options.file
      if (!file) {
        this.$message.error('请选择文件')
        return
      }

      // 模拟文件解析和导入
      this.$message.success(`已成功导入文件: ${file.name}`)
      this.importDialogVisible = false

      // 实际应用中，这里应该解析文件内容，然后添加到问卷中
    },

    // 打开排序对话框
    sortQuestions() {
      // 复制问题数组到可排序数组
      this.sortableQuestions = JSON.parse(JSON.stringify(this.questionnaire.questions))
      this.sortDialogVisible = true
    },

    // 应用排序更改
    applySortChanges() {
      this.questionnaire.questions = this.sortableQuestions
      this.sortDialogVisible = false
      this.$message.success('题目排序已更新')
    },

    // 验证并保存问卷
    validateAndSave(publish) {
      // 验证基本信息
      this.$refs.basicForm.validate(valid => {
        if (!valid) {
          this.activeTab = 'basic'
          this.$message.error('请完善问卷基本信息')
          return
        }

        // 验证是否有题目
        if (this.questionnaire.questions.length === 0) {
          this.activeTab = 'questions'
          this.$message.error('请至少添加一个题目')
          return
        }

        // 验证题目必要数据
        let questionsValid = true
        let invalidQuestionIndex = -1

        for (let i = 0; i < this.questionnaire.questions.length; i++) {
          const question = this.questionnaire.questions[i]

          // 检查题目标题
          if (!question.title || question.title.trim() === '') {
            questionsValid = false
            invalidQuestionIndex = i
            break
          }

          // 检查选项类型题目是否有选项
          if (['radio', 'checkbox'].includes(question.type)) {
            if (question.options.length < 2) {
              questionsValid = false
              invalidQuestionIndex = i
              break
            }

            // 检查选项内容
            for (const option of question.options) {
              if (!option || option.trim() === '') {
                questionsValid = false
                invalidQuestionIndex = i
                break
              }
            }
          }

          // 检查矩阵题是否有行和列
          if (question.type === 'matrix') {
            if (question.rows.length === 0 || question.cols.length < 2) {
              questionsValid = false
              invalidQuestionIndex = i
              break
            }

            // 检查行标题
            for (const row of question.rows) {
              if (!row || row.trim() === '') {
                questionsValid = false
                invalidQuestionIndex = i
                break
              }
            }

            // 检查列选项
            for (const col of question.cols) {
              if (!col || col.trim() === '') {
                questionsValid = false
                invalidQuestionIndex = i
                break
              }
            }
          }
        }

        if (!questionsValid) {
          this.activeTab = 'questions'
          this.$message.error(`第 ${invalidQuestionIndex + 1} 题数据不完整，请检查`)
          return
        }

        // 如果是发布问卷，需要再次确认
        if (publish) {
          this.$confirm('发布后的问卷将对用户可见，确认发布吗？', '发布确认', {
            confirmButtonText: '确认发布',
            cancelButtonText: '取消',
            type: 'warning',
          })
            .then(() => {
              this.saveQuestionnaire(publish)
            })
            .catch(() => {})
        } else {
          this.saveQuestionnaire(publish)
        }
      })
    },

    // 保存问卷到服务器
    saveQuestionnaire(publish) {
      // 更新状态
      if (publish) {
        this.questionnaire.status = 'published'
      } else if (!this.questionnaire.status) {
        this.questionnaire.status = 'draft'
      }

      // 模拟API请求
      const loading = this.$loading({
        lock: true,
        text: publish ? '正在发布...' : '正在保存...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)',
      })

      // 延时模拟网络请求
      setTimeout(() => {
        loading.close()

        // 如果是新建，给一个ID
        if (!this.isEdit || !this.questionnaire.id) {
          this.questionnaire.id = 'QN' + Date.now().toString().substr(-6)
          this.isEdit = true
        }

        // 更新路由参数，添加ID
        if (this.$route.query.id !== this.questionnaire.id) {
          this.$router.replace({
            query: { ...this.$route.query, id: this.questionnaire.id },
          })
        }

        this.$message({
          type: 'success',
          message: publish ? '问卷已成功发布！' : '问卷已保存为草稿！',
        })

        // 实际应用中这里会调用API保存数据
        console.log('保存的问卷数据:', JSON.stringify(this.questionnaire))
      }, 1000)
    },
  },
}
</script>

<style scoped>
.questionnaire-design-container {
  padding: 20px;
}

.page-header-card {
  margin-bottom: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-left h2 {
  margin-right: 10px;
  margin-bottom: 0;
}

.questionnaire-content {
  margin-bottom: 20px;
}

.questionnaire-form {
  max-width: 800px;
}

.form-item-hint {
  margin-left: 10px;
  color: #909399;
}

.questions-toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.right-tools {
  display: flex;
  align-items: center;
}

.question-item {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.question-header .left-section {
  display: flex;
  align-items: center;
}

.drag-handle {
  cursor: move;
  padding: 5px;
  color: #909399;
}

.question-number {
  margin: 0 10px;
  font-weight: bold;
}

.question-settings {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #ebeef5;
}

.logic-jump {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #ebeef5;
}

.logic-rules {
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-top: 10px;
}

.logic-rule-item {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #ebeef5;
}

.logic-rule-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.add-rule {
  margin-top: 10px;
  text-align: right;
}

.option-item {
  margin-bottom: 8px;
}

.other-option {
  margin-top: 10px;
}

.checkbox-settings {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 15px;
}

.min-max-select {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0;
}

.matrix-design {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.matrix-rows,
.matrix-cols {
  flex: 1;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.matrix-title {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #ebeef5;
  font-weight: bold;
}

.matrix-label-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.matrix-label-item .el-input {
  flex: 1;
  margin-right: 10px;
}

.question-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.question-type-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.question-type-item:hover {
  border-color: #409eff;
  background-color: #f0f7ff;
}

.type-icon {
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #409eff;
  color: white;
  border-radius: 50%;
  margin-right: 15px;
}

.type-info {
  flex: 1;
}

.type-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.type-desc {
  font-size: 12px;
  color: #909399;
}

.preview-toolbar {
  text-align: right;
  margin-bottom: 15px;
}

.questionnaire-preview {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  background-color: #fff;
}

.preview-title {
  font-size: 20px;
  margin-bottom: 10px;
  text-align: center;
}

.preview-description {
  color: #606266;
  margin-bottom: 30px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.preview-question-item {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.preview-question-title {
  font-weight: bold;
  margin-bottom: 10px;
}

.preview-question-desc {
  color: #909399;
  font-size: 13px;
  margin-bottom: 10px;
}

.preview-question-content {
  margin-top: 15px;
}

.required-mark {
  color: #f56c6c;
  margin-left: 5px;
}

.preview-option {
  margin-bottom: 8px;
}

.rate-preview {
  padding: 10px 0;
}

.rate-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.number-input-preview {
  display: flex;
  align-items: center;
}

.unit-label {
  margin-left: 10px;
  color: #606266;
}

.matrix-preview {
  margin-top: 10px;
}

.matrix-table {
  width: 100%;
  border-collapse: collapse;
}

.matrix-table th,
.matrix-table td {
  padding: 8px 12px;
  border: 1px solid #ebeef5;
  text-align: center;
}

.matrix-table th {
  background-color: #f5f7fa;
  font-weight: normal;
}

.matrix-radio,
.matrix-checkbox {
  margin: 0;
}

.preview-end-page {
  text-align: center;
  padding: 30px 0;
  border-top: 1px solid #ebeef5;
}

.import-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}

.import-from-file {
  text-align: center;
  padding: 20px 0;
}

.sort-item {
  display: flex;
  align-items: center;
  padding: 10px;
  border: 1px solid #e4e7ed;
  margin-bottom: 10px;
  background-color: #fff;
  cursor: move;
}

.sort-drag-handle {
  margin-right: 10px;
  color: #909399;
}

.sort-number {
  font-weight: bold;
  margin-right: 15px;
  width: 30px;
}

.sort-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
